import React from 'react';
import { Anchor, Box, Button, Heading, Label, Modal, Text, Textarea } from '@zillow/constellation-10';
import { styled } from '@/styled-system/jsx';
import DfpPreviewInFeed from './boostPreviews/DfpPreviewInFeed';
import DfpPreviewInFeedInternal from './boostPreviews/DfpPreviewInFeedInternal';
import { DfpPreviewCustomFlag } from './boostPreviews/DfpPreviewCustomFlag';
import { DfpPreviewSrpNative } from './boostPreviews/DfpPreviewSrpNative';
import { BoostAdPreviews } from './boostPreviews/BoostAdPreviews';
import type { CpmBoostAdPreviewsDto } from '@zg-rentals/rental-manager-env';
import { emailUtils } from '@zg-rentals/rental-manager-env';

const StyledInactiveWrapper = styled('div', {
  base: {
    position: 'relative',
  },
});

const StyledInactiveOverlay = styled('div', {
  base: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(255, 255, 255, 0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  variants: {
    isNotActive: {
      true: {
        display: 'flex',
      },
      false: {
        display: 'none',
      },
    },
  },
});

export const StyledInactiveText = styled(Text, {
  base: {
    padding: 'sm',
    backgroundColor: 'gray.300',
    color: 'white',
  },
});

export interface BoostAdPreviewsModalProps {
  boostAdPreviews: CpmBoostAdPreviewsDto;
  onClose: () => void;
  error?: string;
  listingName?: string;
  isMultiFamily?: boolean;
  onDownloadPdf?: (previewElement: HTMLElement, title: string) => void;
}

export const BoostAdPreviewsModal = ({
  boostAdPreviews: { previews, dfpPreview, isActive },
  onClose,
  error,
  listingName,
  isMultiFamily = true,
  onDownloadPdf,
}: BoostAdPreviewsModalProps) => {
  const previewRef = React.useRef<HTMLDivElement>(null);
  const isInFeed = dfpPreview && dfpPreview.creativeTemplateType === 'IN_FEED';
  const isInFeedInternal = dfpPreview && dfpPreview.creativeTemplateType === 'IN_FEED_INTERNAL';
  const isCustomFlag = dfpPreview && dfpPreview.creativeTemplateType === 'CUSTOM_FLAG';
  const isSrpNative = dfpPreview && dfpPreview.creativeTemplateType === 'SRP_NATIVE';
  const RENTAL_PARTNERS_EMAIL = emailUtils.getHelpEmail(isMultiFamily, false, !isMultiFamily);

  const renderContent = () => {
    if (error) {
      return (
        <Text css={{ color: 'red.400' }} data-test="error">
          Something went wrong. Please, try again later.
        </Text>
      );
    }
    if (previews.length === 0 && !dfpPreview?.creativeTemplateType) {
      return (
        <Text data-test="no-previews" css={{ color: 'red.400' }}>
          No ad available.
        </Text>
      );
    }
    return (
      <Box>
        <Box style={{ paddingBottom: '16px' }}>
          <Text textStyle="body-bold">ZILLOW</Text>
        </Box>
        <div ref={previewRef}>
          <StyledInactiveWrapper>
            {isInFeed && <DfpPreviewInFeed templateValues={dfpPreview.templateValues} />}
            {isInFeedInternal && <DfpPreviewInFeedInternal templateValues={dfpPreview.templateValues} />}
            {isCustomFlag && <DfpPreviewCustomFlag templateValues={dfpPreview.templateValues} />}
            {isSrpNative && <DfpPreviewSrpNative templateValues={dfpPreview.templateValues} />}
            <StyledInactiveOverlay isNotActive={!isActive}>
              {!isActive && <StyledInactiveText textStyle="heading-xs">Ad no longer available</StyledInactiveText>}
            </StyledInactiveOverlay>
          </StyledInactiveWrapper>
        </div>
        <BoostAdPreviews previews={previews} />
        {dfpPreview?.templateValues?.DestinationUrl && (
          <Box style={{ paddingTop: '16px', paddingBottom: '24px' }}>
            <Label>Landing Page URL</Label>
            <Textarea data-test="destination-url" defaultValue={dfpPreview.templateValues.DestinationUrl} />
          </Box>
        )}
        <Box>
          <Text textStyle="body-sm">
            The ad preview is a close representation, but the final appearance may vary slightly across platforms and
            devices. For ad text or URL changes please contact&nbsp;
            <Anchor
              href={`mailto:${RENTAL_PARTNERS_EMAIL}`}
              // @ts-expect-error - Legacy escape prop
              escape={true}
            >
              {RENTAL_PARTNERS_EMAIL}
            </Anchor>
          </Text>
        </Box>
        {onDownloadPdf && (
          <Box style={{ paddingTop: '16px' }}>
            <Button
              onClick={() => {
                if (previewRef.current) {
                  onDownloadPdf(previewRef.current, listingName || 'Boost Ad Preview');
                }
              }}
            >
              Download PDF
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      header={
        listingName && (
          <Heading level={6} css={{ textAlign: 'center' }}>
            {listingName}
          </Heading>
        )
      }
      body={renderContent()}
      size={{ base: 'fullScreen', lg: 'md' }}
    />
  );
};
